import { useMemo } from 'react';
import type { EventPageFragment } from '@graphql/fragments/event.page.graphql.generated';
import { EventPageType } from '@graphql/generated-base';
import type { RoutesPaths } from '@shared/core';
import useQueryString from '@shared/utils/hooks/useQueryString';
import { getMigratedPageSlug } from '@shared/utils/migratedCustomPages';
import { useMigratedCustomPageFeatureFlags } from '@shared/hooks/useMigratedCustomPageFeatureFlags/useMigratedCustomPageFeatureFlags';
import type { MinimalPage } from '@shared/hooks/useDefaultPage';
import { useDefaultPage } from '@shared/hooks/useDefaultPage';
import { CUSTOM_PAGE_PATH, ALLOWLIST_CUSTOM_PAGE_SLUGS } from './GuestSite.constants';

const SUPPORTED_PAGES = [
  EventPageType.welcome,
  EventPageType.story,
  EventPageType.vip,
  EventPageType.faq,
  EventPageType.schedule,
  EventPageType.travel,
  EventPageType.moments,
  EventPageType.registry,
  EventPageType.rsvp,
  EventPageType.tidbits,
  EventPageType.custom,
] as const;

const supportedPagesSet = new Set<EventPageType>(SUPPORTED_PAGES);

export type SupportedEventPages = (typeof SUPPORTED_PAGES)[number];

export type EventPageRoutePaths = Partial<RoutesPaths<SupportedEventPages>>;

export const useEventPageRouting = (eventHandle: string, pages: EventPageFragment[]): EventPageRoutePaths => {
  const { isMigratedCustomPageEnabled } = useMigratedCustomPageFeatureFlags();

  return useMemo(() => {
    if (!eventHandle || !eventHandle.trim().length) {
      return {};
    }
    return pages.reduce((acc, page) => {
      if (supportedPagesSet.has(page.type)) {
        // For migrated pages (like VIP) when feature flag is ON, use the canonical type slug (e.g., 'vip')
        // instead of the page's pageSlug (e.g., 'weddingparty')
        const migratedSlug = getMigratedPageSlug(page.type);
        const slug = (migratedSlug && isMigratedCustomPageEnabled(migratedSlug) ? migratedSlug : undefined) || page.pageSlug;
        acc[page.type as SupportedEventPages] = {
          path: `/${eventHandle}/${slug}`,
          goToPath: () => slug,
        };
      }
      return acc;
    }, {} as EventPageRoutePaths);
  }, [eventHandle, pages, isMigratedCustomPageEnabled]);
};

export const useEventPageRoute = (eventHandle: string, pages?: ReadonlyArray<MinimalPage>) => {
  const { isMigratedCustomPageEnabled } = useMigratedCustomPageFeatureFlags();
  const { isDefaultPage: checkIsDefaultPage } = useDefaultPage(pages);

  const queryString = useQueryString();

  const eventPageRoute = {
    path: `/:eventHandle`,
    goToPath: (type: EventPageType, pageSlug: string) => {
      // Default page (welcome or first page) uses root URL (no slug)
      if (checkIsDefaultPage(pageSlug, type)) {
        let url = `/${eventHandle}`;

        if (queryString) {
          url += `${queryString}`;
        }

        return url;
      }

      // Allowlisted pages are using the short route format (/:eventHandle/:slug) despite being custom pages
      const isAllowlistedSlug = ALLOWLIST_CUSTOM_PAGE_SLUGS.has(pageSlug);
      // Migrated pages with the feature flag enabled are using the short route format (/:eventHandle/:slug)
      const isMigratedPageWithFlagEnabled = isMigratedCustomPageEnabled(pageSlug);

      // Only custom pages that are not allowlisted or migrated
      const useCustomPath = type === EventPageType.custom && !isAllowlistedSlug && !isMigratedPageWithFlagEnabled;

      let url = useCustomPath ? `/${eventHandle}/${CUSTOM_PAGE_PATH}/${pageSlug}` : `/${eventHandle}/${pageSlug}`;

      if (queryString) {
        url += `${queryString}`;
      }

      return url;
    },
  };
  return eventPageRoute;
};
